import dayjs from 'dayjs'
import React, { ReactNode, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { TimeFrame } from 'types/analytics'
import { formatTimeframeLabel, TIMEFRAME_OPTIONS } from 'utils/analytics'
import { ChartSeriesPoint } from 'utils/analytics/analyticsChartSeries'

import ChartSummary from './Summary'
import TimeframeButton from './TimeframeButton'
import ZoomControls from './ZoomControls'

type RawChartPoint = { time: string; value: number; label?: string; t?: number; index?: number }

const CHART_MARGINS = { top: 5, right: 16, left: 8, bottom: 5 }
const PIXELS_PER_POINT = 1.8

const calculatePercentChange = (previous?: number, current?: number) => {
  if (previous === undefined || current === undefined) return undefined
  if (!Number.isFinite(previous) || !Number.isFinite(current) || previous === 0) return undefined
  return ((current - previous) / Math.abs(previous)) * 100
}

interface ChartSummaryConfig {
  title: string
  changePercent?: number
  timeframeLabel?: string
  isPrice?: boolean
  valueFormatter?: (value?: number) => string
}

export interface LineChartProps {
  data: any[]
  color?: string
  height?: number | string
  minHeight?: number
  className?: string
  summaryProps: ChartSummaryConfig
  defaultLabel?: string
  defaultValue?: number
  topRight?: ReactNode
  bottomLeft?: ReactNode
  bottomRight?: ReactNode
  useCompactYAxis?: boolean
  showTimeSelector?: boolean
  selectedTimeframe?: TimeFrame
  onTimeframeChange?: (timeframe: TimeFrame) => void
  onLatestPointVisibleChange?: (visible: boolean) => void
  showZoomControls?: boolean
  resetKey?: string
  hideXAxis?: boolean
  hideYAxis?: boolean
  domain?: [number | 'auto' | 'dataMin' | 'dataMax', number | 'auto' | 'dataMin' | 'dataMax']
  padding?: { top: number; bottom: number }
  gradientOpacity?: number
}

function LineChart({
  data,
  color = '#3083dc',
  height,
  summaryProps,
  defaultLabel,
  defaultValue,
  topRight,
  bottomLeft,
  bottomRight,
  minHeight,
  className,
  useCompactYAxis,
  showTimeSelector = false,
  selectedTimeframe,
  onTimeframeChange,
  onLatestPointVisibleChange,
  showZoomControls = true,
  resetKey,
  hideXAxis,
  hideYAxis,
  domain,
  padding,
  gradientOpacity = 0.5,
}: LineChartProps) {
  const lastHoverIndex = useRef<number | null>(null)
  const hoverStateRef = useRef<{ index: number | null; value?: number; label?: string; change?: number }>({
    index: null,
  })
  const containerRef = useRef<HTMLDivElement>(null)
  const [containerWidth, setContainerWidth] = useState(400)
  const [hoverPoint, setHoverPoint] = useState<{ value?: number; label?: string } | null>(null)
  const [isLatestVisible, setIsLatestVisible] = useState(true)
  const [hoverChangePercent, setHoverChangePercent] = useState<number | undefined>(undefined)

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (entry?.contentRect) {
        setContainerWidth(entry.contentRect.width)
      }
    })

    observer.observe(container)
    return () => observer.disconnect()
  }, [])

  const normalizedData = useMemo<ChartSeriesPoint[]>(() => {
    if (!Array.isArray(data) || data.length === 0) return []

    const typed = data as ChartSeriesPoint[]
    let needsCopy = false
    for (const point of typed) {
      if (!point || typeof point.t !== 'number' || typeof point.label !== 'string' || typeof point.index !== 'number') {
        needsCopy = true
        break
      }
    }

    if (!needsCopy) {
      return typed
    }

    const mapped = (data as RawChartPoint[])
      .filter((point): point is RawChartPoint => Boolean(point))
      .map((point, index) => {
        const timestamp = typeof point.t === 'number' ? point.t : +new Date(point.time)
        return {
          ...point,
          t: timestamp,
          label: point.label ?? dayjs(timestamp).format('MMM D, YYYY'),
          index: typeof point.index === 'number' ? point.index : index,
        }
      })

    return mapped as ChartSeriesPoint[]
  }, [data])

  const buildAggregate = useCallback((series: ChartSeriesPoint[], unit: 'week' | 'month') => {
    if (!series.length) return [] as ChartSeriesPoint[]

    const buckets = new Map<number, ChartSeriesPoint>()

    series.forEach((point) => {
      const bucketStart = dayjs(point.t).startOf(unit).valueOf()
      const existing = buckets.get(bucketStart)

      if (!existing || point.t > existing.t) {
        buckets.set(bucketStart, { ...point })
      }
    })

    const entries = Array.from(buckets.entries()).sort((a, b) => a[0] - b[0])
    const now = dayjs()

    return entries.map(([bucketStart, point], index) => {
      const timeframe = unit === 'week' ? 'W' : 'M'
      return {
        ...point,
        index,
        label: formatTimeframeLabel(dayjs(bucketStart), timeframe, {
          isLatest: index === entries.length - 1,
          now,
        }),
      }
    })
  }, [])

  const aggregatedData = useMemo(() => {
    const weekly = buildAggregate(normalizedData, 'week')
    const monthly = buildAggregate(normalizedData, 'month')

    return {
      D: normalizedData,
      W: weekly,
      M: monthly,
    }
  }, [buildAggregate, normalizedData])

  const [internalTimeframe, setInternalTimeframe] = useState<TimeFrame>('D')

  useEffect(() => {
    if (selectedTimeframe) {
      setInternalTimeframe(selectedTimeframe)
    }
  }, [selectedTimeframe])

  const activeTimeframe = selectedTimeframe ?? internalTimeframe

  const handleTimeframeChange = useCallback(
    (next: TimeFrame) => {
      if (onTimeframeChange) {
        onTimeframeChange(next)
      } else {
        setInternalTimeframe(next)
      }
    },
    [onTimeframeChange]
  )

  const baseData = useMemo(() => aggregatedData[activeTimeframe] ?? [], [activeTimeframe, aggregatedData])

  const totalCount = baseData.length
  const ZOOM_STEP_RATIO = 0.3
  const PAN_STEP_RATIO = 0.4

  const getInitialMaxWindow = () => {
    const maxPoints = Math.floor(containerWidth / PIXELS_PER_POINT)
    return Math.max(10, Math.min(maxPoints, totalCount))
  }

  const rangeMetaRef = useRef<{ count: number }>({ count: totalCount })

  const [visibleRange, setVisibleRange] = useState(() => {
    if (totalCount === 0) return { start: 0, end: 0 }
    const initialWindow = getInitialMaxWindow()
    const start = Math.max(0, totalCount - initialWindow)
    return { start, end: totalCount - 1 }
  })

  useEffect(() => {
    const prevCount = rangeMetaRef.current.count

    setVisibleRange((prevRange) => {
      const endIndex = totalCount > 0 ? totalCount - 1 : 0

      if (totalCount === 0) {
        rangeMetaRef.current.count = totalCount
        return prevRange.start === 0 && prevRange.end === 0 ? prevRange : { start: 0, end: 0 }
      }

      const prevFullRange = prevCount <= 1 || (prevRange.start === 0 && prevRange.end === prevCount - 1)

      let nextStart: number
      let nextEnd: number

      if (prevFullRange) {
        nextStart = 0
        nextEnd = endIndex
      } else {
        const denom = Math.max(prevCount - 1, 1)
        const startRatio = prevRange.start / denom
        const endRatio = prevRange.end / denom
        nextStart = Math.max(0, Math.min(endIndex, Math.round(startRatio * endIndex)))
        nextEnd = Math.max(nextStart, Math.min(endIndex, Math.round(endRatio * endIndex)))
      }

      rangeMetaRef.current.count = totalCount

      if (prevRange.start === nextStart && prevRange.end === nextEnd) {
        return prevRange
      }

      return { start: nextStart, end: nextEnd }
    })
  }, [activeTimeframe, totalCount])

  // Recalculate visible range when container width changes to ensure correct initial zoom
  useEffect(() => {
    if (totalCount === 0) return

    setVisibleRange((prevRange) => {
      const maxWindow = Math.max(10, Math.min(Math.floor(containerWidth / PIXELS_PER_POINT), totalCount))
      const currentWindow = prevRange.end - prevRange.start + 1

      const isAtMaxZoom = currentWindow >= totalCount * 0.9

      if (isAtMaxZoom) {
        const start = Math.max(0, totalCount - maxWindow)
        return { start, end: totalCount - 1 }
      }

      return prevRange
    })
  }, [containerWidth, totalCount])

  const chartData = useMemo(() => {
    if (activeTimeframe !== 'D') return baseData
    if (!totalCount) return []

    const maxIndex = totalCount - 1
    const clampedStart = Math.max(0, Math.min(visibleRange.start, maxIndex))
    const clampedEnd = Math.max(clampedStart, Math.min(visibleRange.end, maxIndex))
    const windowed = baseData.slice(clampedStart, clampedEnd + 1)

    // No downsampling - show all points in visible range
    return windowed
  }, [activeTimeframe, baseData, totalCount, visibleRange])
  const chartDataPositionLookup = useMemo(() => {
    const lookup = new Map<number, number>()
    chartData.forEach((point, position) => {
      if (typeof point.index === 'number') {
        lookup.set(point.index, position)
      }
    })
    return lookup
  }, [chartData])

  const showsLatestPoint = useMemo(() => {
    if (!baseData.length) return false
    if (!chartData.length) return true
    const lastBase = baseData[baseData.length - 1]
    const lastVisible = chartData[chartData.length - 1]
    return dayjs(lastBase.time).isSame(lastVisible.time)
  }, [baseData, chartData])

  useEffect(() => {
    onLatestPointVisibleChange?.(showsLatestPoint)
    setIsLatestVisible(showsLatestPoint || activeTimeframe !== 'D')
  }, [showsLatestPoint, activeTimeframe, onLatestPointVisibleChange])

  const tickFormatter = useCallback(
    (t: number) => dayjs(t).format(activeTimeframe === 'M' ? 'MMM' : 'MMM DD'),
    [activeTimeframe]
  )

  const yAxisFormatter = useCallback(
    (value: number) => {
      if (useCompactYAxis) {
        if (value >= 1e6) return `$${(value / 1e9).toFixed(1)}B`
      } else {
        if (value >= 1e9) return `$${(value / 1e9).toFixed(0)}B`
        if (value >= 1e6) return `$${(value / 1e6).toFixed(0)}M`
      }

      if (value >= 1e3) return `$${(value / 1e3).toFixed(0)}K`
      return `$${value.toFixed(0)}`
    },
    [useCompactYAxis]
  )

  const defaultPoint = useMemo(() => {
    if (!baseData.length) return undefined
    return baseData[baseData.length - 1]
  }, [baseData])

  const rawId = useId()
  const gradientId = useMemo(() => `linechart-gradient-${rawId.replace(/[:]/g, '')}`, [rawId])

  const fallbackLabel = useMemo(() => {
    if (defaultLabel !== undefined) return defaultLabel
    if (activeTimeframe !== 'D') return defaultPoint?.label
    return undefined
  }, [defaultLabel, activeTimeframe, defaultPoint?.label])

  const fallbackValue = useMemo(() => {
    if (activeTimeframe === 'D' && typeof defaultValue === 'number') return defaultValue
    return defaultPoint?.value
  }, [activeTimeframe, defaultPoint?.value, defaultValue])

  useEffect(() => {
    lastHoverIndex.current = null
    setHoverPoint(null)
    setHoverChangePercent(undefined)
    hoverStateRef.current = { index: null }
  }, [fallbackValue, fallbackLabel, resetKey])

  const handleMouseLeave = useCallback(() => {
    lastHoverIndex.current = null
    setHoverChangePercent(undefined)
    setHoverPoint(null)
    hoverStateRef.current = { index: null }
  }, [])

  const handleMouseMove = useCallback(
    (state: any | undefined) => {
      const payload = state?.activePayload?.[0]?.payload as ChartSeriesPoint | undefined
      if (!payload) return

      const payloadIndex = typeof payload.index === 'number' ? payload.index : null
      const lookupPosition = payloadIndex !== null ? chartDataPositionLookup.get(payloadIndex) : undefined
      let pointPosition =
        lookupPosition !== undefined ? lookupPosition : chartData.findIndex((point) => point === payload)
      if (pointPosition < 0 && payloadIndex !== null) {
        pointPosition = chartData.findIndex((point) => point.index === payloadIndex)
      }
      if (pointPosition < 0) {
        return
      }

      const previousPoint = pointPosition > 0 ? chartData[pointPosition - 1] : undefined
      const change = pointPosition > 0 ? calculatePercentChange(previousPoint?.value, payload.value) : undefined

      const nextHoverState = {
        index: payloadIndex ?? chartData[pointPosition]?.index ?? null,
        value: payload.value,
        label: payload.label,
        change,
      }

      const { index, value, label, change: prevChange } = hoverStateRef.current
      if (
        index === nextHoverState.index &&
        value === nextHoverState.value &&
        label === nextHoverState.label &&
        prevChange === change
      ) {
        return
      }

      hoverStateRef.current = nextHoverState
      lastHoverIndex.current = nextHoverState.index
      setHoverPoint({ value: payload.value, label: payload.label })
      setHoverChangePercent(change)
    },
    [chartData, chartDataPositionLookup]
  )

  const timeSelector = useMemo(() => {
    if (!showTimeSelector) return null

    return (
      <div className="flex gap-1">
        {TIMEFRAME_OPTIONS.map((timeframe) => (
          <TimeframeButton
            key={timeframe}
            value={timeframe}
            onChange={(next) => handleTimeframeChange(next as TimeFrame)}
            isSelected={activeTimeframe === timeframe}
          />
        ))}
      </div>
    )
  }, [activeTimeframe, handleTimeframeChange, showTimeSelector])

  const { minWindow, maxWindow } = useMemo(() => {
    const maxPoints = Math.floor(containerWidth / PIXELS_PER_POINT)
    const max = Math.max(10, Math.min(maxPoints, totalCount))

    const min = Math.max(10, Math.min(Math.floor(max / 4), totalCount))

    return { minWindow: min, maxWindow: max }
  }, [totalCount, containerWidth])

  const zoomControls =
    showZoomControls && activeTimeframe === 'D' && totalCount > 0 ? (
      <ZoomControls
        totalCount={totalCount}
        visibleRange={visibleRange}
        setVisibleRange={setVisibleRange}
        minWindow={minWindow}
        maxWindow={maxWindow}
        zoomRatio={ZOOM_STEP_RATIO}
        panRatio={PAN_STEP_RATIO}
      />
    ) : null

  const containerStyle = useMemo(() => {
    const base: React.CSSProperties = {
      transform: 'translateZ(0)',
      willChange: 'transform',
    }
    if (typeof minHeight === 'number') {
      base.minHeight = `${minHeight}px`
    }
    return base
  }, [minHeight])

  const chartHeight = height ?? minHeight ?? 300

  useEffect(() => {
    if (activeTimeframe !== 'D') {
      setIsLatestVisible(true)
    }
  }, [activeTimeframe])

  const timeframeChangePercent = useMemo(() => {
    if (chartData.length < 2) return undefined
    const latest = chartData[chartData.length - 1]?.value
    const previous = chartData[chartData.length - 2]?.value
    return calculatePercentChange(previous, latest)
  }, [chartData])

  const shouldShowBaseChange = activeTimeframe !== 'D' || isLatestVisible
  const baseChangeValue = shouldShowBaseChange ? timeframeChangePercent : undefined

  const summary = useMemo(() => {
    const { title, timeframeLabel, isPrice, valueFormatter } = summaryProps
    const summaryValue = hoverPoint?.value ?? fallbackValue
    const summaryLabel = hoverPoint?.label ?? fallbackLabel
    return (
      <ChartSummary
        title={title}
        value={summaryValue}
        label={summaryLabel}
        timeframe={timeframeLabel}
        changePercent={baseChangeValue}
        hoverChangePercent={hoverChangePercent}
        isPrice={isPrice}
        valueFormatter={valueFormatter}
        showChange
      />
    )
  }, [summaryProps, hoverPoint, fallbackValue, fallbackLabel, baseChangeValue, hoverChangePercent])

  return (
    <div
      ref={containerRef}
      className={`w-full min-w-0 p-4 flex flex-col  bg-gray-900 rounded-lg border border-gray-700 [&>*]:text-base ${
        className ?? ''
      }`.trim()}
      style={containerStyle}
    >
      <div className="flex justify-between items-start mb-3 empty:hidden">
        <div>{summary}</div>
        <div className="flex flex-col gap-3 items-end">
          <div className="flex flex-col gap-2 items-end">
            {timeSelector}
            {zoomControls}
          </div>
          <div>{topRight ?? null}</div>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={chartHeight}>
        <AreaChart
          data={chartData}
          margin={hideXAxis && hideYAxis ? { top: 0, right: 0, left: 0, bottom: 0 } : CHART_MARGINS}
          onMouseLeave={handleMouseLeave}
          onMouseMove={handleMouseMove}
        >
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={color} stopOpacity={gradientOpacity} />
              <stop offset="100%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          {!hideXAxis && (
            <XAxis
              dataKey="t"
              axisLine={false}
              tickLine={false}
              tickFormatter={tickFormatter}
              minTickGap={10}
              allowDataOverflow={false}
              fontSize={12}
              fontWeight={500}
            />
          )}
          {!hideYAxis && (
            <YAxis
              axisLine={false}
              tickLine={false}
              tickFormatter={yAxisFormatter}
              width={56}
              allowDataOverflow={false}
              tick={{ fontSize: 12, fill: '#888' }}
              domain={domain}
              padding={padding}
            />
          )}
          {hideYAxis && <YAxis domain={domain} padding={padding} hide />}
          <Tooltip
            cursor={{ stroke: '#374151', strokeOpacity: 0.1 }}
            contentStyle={{ display: 'none' }}
            isAnimationActive={false}
            animationDuration={0}
          />
          <Area
            dataKey="value"
            type="linear" // linear keeps transitions cheap
            stroke={color}
            fill={`url(#${gradientId})`}
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
            animationDuration={0}
            connectNulls
          />
        </AreaChart>
      </ResponsiveContainer>

      <div className="flex justify-between">
        {bottomLeft ?? null}
        {bottomRight ?? null}
      </div>
    </div>
  )
}

export default React.memo(LineChart, (prevProps, nextProps) => {
  return (
    prevProps.data === nextProps.data &&
    prevProps.color === nextProps.color &&
    prevProps.height === nextProps.height &&
    prevProps.minHeight === nextProps.minHeight &&
    prevProps.summaryProps === nextProps.summaryProps &&
    prevProps.defaultLabel === nextProps.defaultLabel &&
    prevProps.defaultValue === nextProps.defaultValue &&
    prevProps.useCompactYAxis === nextProps.useCompactYAxis &&
    prevProps.showTimeSelector === nextProps.showTimeSelector &&
    prevProps.showZoomControls === nextProps.showZoomControls &&
    prevProps.selectedTimeframe === nextProps.selectedTimeframe &&
    prevProps.onTimeframeChange === nextProps.onTimeframeChange &&
    prevProps.onLatestPointVisibleChange === nextProps.onLatestPointVisibleChange &&
    prevProps.className === nextProps.className &&
    prevProps.topRight === nextProps.topRight &&
    prevProps.bottomLeft === nextProps.bottomLeft &&
    prevProps.bottomRight === nextProps.bottomRight &&
    prevProps.hideXAxis === nextProps.hideXAxis &&
    prevProps.hideYAxis === nextProps.hideYAxis &&
    prevProps.domain === nextProps.domain &&
    prevProps.padding === nextProps.padding &&
    prevProps.gradientOpacity === nextProps.gradientOpacity
  )
})
